import FilterIcon from '@mui/icons-material/TuneOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import Button from '@/components/Button';
import GenericDrawer from '@/components/GenericDrawer';
import { AgaveDataGrid } from '@/components/GenericTable/DataGridStyled';
import DownloadModal from '@/components/Modals/DownloadModal/DownloadModal';
import SearchInput from '@/components/SearchInput';

import type { IFalabellaOrder } from '@/types/plugins/falabella';

import Filters from './dependencies/Filters';
import DetailsModal from './dependencies/modals/DetailsModal';
import { falabellaColumns } from './falabella-columns';
import useFalabella from './useFalabella';

const FalabellaSection = () => {
  const { t } = useTranslation('falabella');
  const theme = useTheme();
  const hooks = useFalabella();

  const columns = falabellaColumns(
    t,
    hooks.onOpenDetails,
    hooks.onOpenDownloadModal,
    hooks.onCreateOrder,
    hooks.creatingOrderId,
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      width="100%"
      alignItems="end"
      justifyContent="center"
      gap="1rem"
    >
      <Paper
        elevation={3}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1rem',
          padding: `${theme.spacing(4)} 0 0`,
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          width="100%"
          height="auto"
          sx={{
            padding: `0 ${theme.spacing(4)}`,
            flexDirection: { xxs: 'column', xl: 'row' },
            gap: { xxs: '1rem', xl: 0 },
            alignItems: 'center',
          }}
        >
          <SearchInput
            buttonText={t('filters.search', 'Buscar')}
            placeholder={t('filters.searchPlaceholder', 'Buscar por Orden o LPN...')}
            onChange={(e) => hooks.onChangeSearch(e.target.value)}
            value={hooks.searchValue}
            onClick={hooks.onSearch}
            handleKeyPress={(e) => {
              if (e.key === 'Enter') hooks.onSearch();
            }}
            sx={{ width: '100%' }}
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xxs: 'column', xs: 'row' },
              gap: '1rem',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Button variant="outlined" onClick={hooks.onClearFilters} size="small">
              {t('filters.clear', 'Borrar filtros')}
            </Button>
            <Box
              display="flex"
              gap="0.5rem"
              alignItems="center"
              justifyContent="center"
              onClick={hooks.onOpenFilters}
              sx={{
                cursor: 'pointer',
                ':hover': { backgroundColor: theme.palette.primary.lighter },
                padding: '0.5rem',
                borderRadius: '1rem',
              }}
            >
              <FilterIcon color="primary" />

              <Typography variant="body1" fontWeight={theme.typography.fontWeightMedium}>
                {t('filters.title', 'Filtros')}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box width="100%" mt={2}>
          <Box sx={{ overflowX: 'auto', width: '100%' }}>
            <Box sx={{ minWidth: '700px' }}>
              <AgaveDataGrid
                columns={columns}
                rows={hooks.orders}
                loading={hooks.loading}
                rowCount={hooks.totalOrders}
                paginationModel={hooks.paginationModel}
                onPaginationModelChange={hooks.setPaginationModel}
                getRowId={(row: IFalabellaOrder) => row.id}
                localeText={{
                  noRowsLabel: t('table.noRows', 'No hay órdenes disponibles'),
                  noResultsOverlayLabel: t('table.noResults', 'No se encontraron resultados'),
                }}
                onCellClick={(params) => {
                  if (!['actions', '__check__'].includes(params.colDef.field)) {
                    hooks.onOpenDetails(params.row);
                  }
                }}
                paginationMode="server"
                disableRowSelectionOnClick
                disableColumnMenu
                hideFooterSelectedRowCount
              />
            </Box>
          </Box>
        </Box>
      </Paper>

      <GenericDrawer
        open={hooks.openFilters}
        onClose={hooks.onCloseFilters}
        title={t('filters.title', 'Filtros')}
        disableEscapeKeyDown={true}
        iconTitle={<FilterIcon color="primary" />}
      >
        <Filters
          selectedFilters={hooks.tempFilters}
          onChange={hooks.onChangeDrawerFilter}
          clearFilters={hooks.onClearFilters}
          applyFilters={hooks.onApplyFilters}
        />
      </GenericDrawer>

      <DetailsModal
        isOpen={hooks.isDetailsModalOpen}
        onClose={hooks.onCloseDetails}
        order={hooks.selectedOrder}
        onDownloadLabel={hooks.onOpenDownloadModal}
        onCreateOrder={hooks.onCreateOrder}
        isCreating={
          hooks.creatingOrderId === (hooks.selectedOrder?.orderFalabella || hooks.selectedOrder?.id)
        }
      />

      <DownloadModal
        isOpen={hooks.downloadModalOpen}
        onClose={hooks.onCloseDownloadModal}
        trackingIds={hooks.selectedTrackingId ? [hooks.selectedTrackingId] : []}
        downloadLabels={hooks.downloadLabels}
        amount
      />
    </Box>
  );
};

export default FalabellaSection;
